import * as THREE from 'three';
import type { Enemy } from '@entities/Enemy';
import type { NPC } from '@entities/NPC';

export class TargetingSystem {
  private camera: THREE.Camera;
  private raycaster: THREE.Raycaster;
  private currentTarget: Enemy | null = null;
  private targetIndicator: THREE.Mesh | null = null;
  private scene: THREE.Scene;

  // Tab targeting
  private enemies: Enemy[] = [];
  private tabTargetIndex: number = -1;
  private lastTabTime: number = 0;
  private tabResetDelay: number = 3000; // Reset tab cycle after 3 seconds

  // PERF: Reusable vector for mouse coords and position lookups
  private readonly _mouseVec: THREE.Vector2 = new THREE.Vector2();
  private readonly _tempPos: THREE.Vector3 = new THREE.Vector3();

  constructor(camera: THREE.Camera, scene: THREE.Scene) {
    this.camera = camera;
    this.scene = scene;
    this.raycaster = new THREE.Raycaster();
    // PERF: Limit raycaster to near objects only
    this.raycaster.far = 50;

    // Create target indicator (ring around enemy)
    this.createTargetIndicator();

    // Listen for Tab key
    window.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    if (event.code === 'Tab') {
      event.preventDefault();
      this.cycleTarget(event.shiftKey);
    } else if (event.code === 'Escape') {
      this.clearTarget();
    }
  }

  public setEnemies(enemies: Enemy[]): void {
    this.enemies = enemies;
  }

  public cycleTarget(reverse: boolean = false): void {
    const now = Date.now();

    // Reset cycle if too much time has passed
    if (now - this.lastTabTime > this.tabResetDelay) {
      this.tabTargetIndex = -1;
    }
    this.lastTabTime = now;

    // PERF: Build sorted array without chained filter/map/sort
    const camPos = this.camera.position;
    const aliveEnemies: { enemy: Enemy; dist: number }[] = [];

    for (let i = 0; i < this.enemies.length; i++) {
      const enemy = this.enemies[i];
      if (!enemy.isAlive()) continue;

      // Use mesh position directly instead of clone
      const pos = enemy.mesh.position;
      const dx = pos.x - camPos.x;
      const dy = pos.y - camPos.y;
      const dz = pos.z - camPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < 40) {
        aliveEnemies.push({ enemy, dist });
      }
    }

    if (aliveEnemies.length === 0) {
      this.clearTarget();
      return;
    }

    // Sort by distance
    aliveEnemies.sort((a, b) => a.dist - b.dist);

    // Cycle through enemies
    if (reverse) {
      this.tabTargetIndex--;
      if (this.tabTargetIndex < 0) {
        this.tabTargetIndex = aliveEnemies.length - 1;
      }
    } else {
      this.tabTargetIndex++;
      if (this.tabTargetIndex >= aliveEnemies.length) {
        this.tabTargetIndex = 0;
      }
    }

    this.setTarget(aliveEnemies[this.tabTargetIndex].enemy);
  }

  public getClosestEnemy(): Enemy | null {
    const camPos = this.camera.position;
    let closest: Enemy | null = null;
    let closestDistSq = 40 * 40; // Use squared distance

    for (let i = 0; i < this.enemies.length; i++) {
      const enemy = this.enemies[i];
      if (!enemy.isAlive()) continue;

      // PERF: Use mesh position directly, squared distance
      const pos = enemy.mesh.position;
      const dx = pos.x - camPos.x;
      const dy = pos.y - camPos.y;
      const dz = pos.z - camPos.z;
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < closestDistSq) {
        closestDistSq = distSq;
        closest = enemy;
      }
    }

    return closest;
  }

  private createTargetIndicator(): void {
    // PERF: Reduced segments from 32 to 16
    const geometry = new THREE.RingGeometry(0.8, 1, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.7,
    });
    this.targetIndicator = new THREE.Mesh(geometry, material);
    this.targetIndicator.rotation.x = -Math.PI / 2;
    this.targetIndicator.visible = false;
    this.scene.add(this.targetIndicator);
  }

  public raycastFromMouse(
    mouseX: number,
    mouseY: number,
    enemies: Enemy[]
  ): Enemy | null {
    // PERF: Reuse vector instead of allocating new one
    this._mouseVec.set(
      (mouseX / window.innerWidth) * 2 - 1,
      -(mouseY / window.innerHeight) * 2 + 1
    );

    this.raycaster.setFromCamera(this._mouseVec, this.camera);

    // PERF: Build mesh array without filter/map chain for alive enemies
    const enemyMeshes: THREE.Object3D[] = [];
    for (let i = 0; i < enemies.length; i++) {
      if (enemies[i].isAlive()) {
        enemyMeshes.push(enemies[i].mesh);
      }
    }

    const intersects = this.raycaster.intersectObjects(enemyMeshes, true);

    if (intersects.length > 0) {
      // Find which enemy was hit
      const hitObject = intersects[0].object;
      for (const enemy of enemies) {
        if (enemy.mesh === hitObject || enemy.mesh.children.includes(hitObject)) {
          return enemy;
        }
        // Check if hit object is a descendant
        let parent = hitObject.parent;
        while (parent) {
          if (parent === enemy.mesh) {
            return enemy;
          }
          parent = parent.parent;
        }
      }
    }

    return null;
  }

  public raycastNPCFromMouse(
    mouseX: number,
    mouseY: number,
    npcs: NPC[]
  ): NPC | null {
    // PERF: Reuse vector
    this._mouseVec.set(
      (mouseX / window.innerWidth) * 2 - 1,
      -(mouseY / window.innerHeight) * 2 + 1
    );

    this.raycaster.setFromCamera(this._mouseVec, this.camera);

    // PERF: Build mesh array with for loop
    const npcMeshes: THREE.Object3D[] = [];
    for (let i = 0; i < npcs.length; i++) {
      npcMeshes.push(npcs[i].mesh);
    }

    const intersects = this.raycaster.intersectObjects(npcMeshes, true);

    if (intersects.length > 0) {
      // Find which NPC was hit
      const hitObject = intersects[0].object;
      for (const npc of npcs) {
        if (npc.mesh === hitObject || npc.mesh.children.includes(hitObject)) {
          return npc;
        }
        // Check if hit object is a descendant
        let parent = hitObject.parent;
        while (parent) {
          if (parent === npc.mesh) {
            return npc;
          }
          parent = parent.parent;
        }
      }
    }

    return null;
  }

  public setTarget(enemy: Enemy | null): void {
    this.currentTarget = enemy;

    if (enemy && this.targetIndicator) {
      this.targetIndicator.visible = true;
    } else if (this.targetIndicator) {
      this.targetIndicator.visible = false;
    }
  }

  public getTarget(): Enemy | null {
    return this.currentTarget;
  }

  public clearTarget(): void {
    this.currentTarget = null;
    if (this.targetIndicator) {
      this.targetIndicator.visible = false;
    }
  }

  public update(): void {
    // Update target indicator position
    if (this.currentTarget && this.targetIndicator) {
      if (!this.currentTarget.isAlive()) {
        this.clearTarget();
        return;
      }

      // PERF: Use mesh position directly instead of clone
      const pos = this.currentTarget.mesh.position;
      this.targetIndicator.position.x = pos.x;
      this.targetIndicator.position.y = 0.05;
      this.targetIndicator.position.z = pos.z;

      // Rotate indicator
      this.targetIndicator.rotation.z += 0.02;
    }
  }

  public destroy(): void {
    if (this.targetIndicator) {
      this.scene.remove(this.targetIndicator);
    }
  }
}
